var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { StreamPartIDUtils, toUserId, toUserIdRaw } from '@streamr/utils';
import { AsymmetricEncryptionType, GroupKeyResponse, GroupKeyRequest, SignatureType, ContentType, EncryptionType } from '@streamr/trackerless-network';
import without from 'lodash/without';
import { Lifecycle, inject, scoped } from 'tsyringe';
import { Identity, IdentityInjectionToken } from '../identity/Identity';
import { NetworkNodeFacade } from '../NetworkNodeFacade';
import { StreamRegistry } from '../contracts/StreamRegistry';
import { StreamrClientEventEmitter } from '../events';
import { MessageID } from '../protocol/MessageID';
import { StreamMessageType } from '../protocol/StreamMessage';
import { createRandomMsgChainId } from '../publish/messageChain';
import { MessageSigner } from '../signature/MessageSigner';
import { SignatureValidator } from '../signature/SignatureValidator';
import { LoggerFactory } from '../utils/LoggerFactory';
import { validateStreamMessage } from '../utils/validateStreamMessage';
import { EncryptionUtil } from './EncryptionUtil';
import { LocalGroupKeyStore } from './LocalGroupKeyStore';
import { ConfigInjectionToken } from '../ConfigTypes';
import { isCompliantAsymmetricEncryptionType } from '../utils/encryptionCompliance';
import { StreamrClientError } from '../StreamrClientError';
/*
 * Sends group key responses
 */
var ResponseType;
(function (ResponseType) {
    ResponseType[ResponseType["NONE"] = 0] = "NONE";
    ResponseType[ResponseType["NORMAL"] = 1] = "NORMAL";
    ResponseType[ResponseType["ERC_1271"] = 2] = "ERC_1271";
})(ResponseType || (ResponseType = {}));
let PublisherKeyExchange = class PublisherKeyExchange {
    networkNodeFacade;
    streamRegistry;
    signatureValidator;
    messageSigner;
    store;
    identity;
    logger;
    erc1271Publishers = new Set();
    config;
    constructor(networkNodeFacade, streamRegistry, signatureValidator, messageSigner, store, identity, config, eventEmitter, loggerFactory) {
        this.networkNodeFacade = networkNodeFacade;
        this.streamRegistry = streamRegistry;
        this.signatureValidator = signatureValidator;
        this.messageSigner = messageSigner;
        this.store = store;
        this.identity = identity;
        this.logger = loggerFactory.createLogger('PublisherKeyExchange');
        this.config = config;
        // Setting explicit keys disables the key-exchange
        if (config.encryption.keys === undefined) {
            networkNodeFacade.once('start', async () => {
                networkNodeFacade.addMessageListener((msg) => this.onMessage(msg));
                this.logger.debug('Started');
            });
            eventEmitter.on('messagePublished', (msg) => {
                if (msg.signatureType === SignatureType.ERC_1271) {
                    const publisherId = msg.getPublisherId();
                    if (!this.erc1271Publishers.has(publisherId)) {
                        this.logger.debug('Add ERC-1271 publisher', { publisherId });
                        this.erc1271Publishers.add(publisherId);
                    }
                }
            });
        }
    }
    async onMessage(request) {
        if (request.messageType === StreamMessageType.GROUP_KEY_REQUEST) {
            try {
                const { recipientId, requestId, publicKey, groupKeyIds, encryptionType: keyEncryptionType } = GroupKeyRequest.fromBinary(request.content);
                const recipientUserId = toUserId(recipientId);
                if (!isCompliantAsymmetricEncryptionType(keyEncryptionType, this.config)) {
                    throw new StreamrClientError(`EncryptionType in key request (${keyEncryptionType}) is not compliant with encryption settings!`, 'ENCRYPTION_POLICY_VIOLATION', request);
                }
                const responseType = await this.getResponseType(recipientUserId);
                if (responseType !== ResponseType.NONE) {
                    this.logger.debug('Handling group key request', { requestId, responseType, keyEncryptionType: AsymmetricEncryptionType[keyEncryptionType] });
                    await validateStreamMessage(request, this.streamRegistry, this.signatureValidator, this.config);
                    const authenticatedUser = await this.identity.getUserId();
                    const keys = without(await Promise.all(groupKeyIds.map((id) => this.store.get(id, authenticatedUser))), undefined);
                    if (keys.length > 0) {
                        const response = await this.createResponse(keys, responseType, recipientUserId, request.getStreamPartID(), publicKey, request.getPublisherId(), requestId, keyEncryptionType);
                        await this.networkNodeFacade.broadcast(response);
                        this.logger.debug('Handled group key request (found keys)', {
                            groupKeyIds: keys.map((k) => k.id).join(),
                            recipient: request.getPublisherId()
                        });
                    }
                    else {
                        this.logger.debug('Handled group key request (no keys found)', {
                            requestId,
                            recipient: request.getPublisherId()
                        });
                    }
                }
            }
            catch (err) {
                this.logger.debug('Failed to handle group key request', err);
            }
        }
    }
    async getResponseType(publisherId) {
        const myId = await this.identity.getUserId();
        if (publisherId === myId) {
            return ResponseType.NORMAL;
        }
        else if (this.erc1271Publishers.has(publisherId)) {
            return ResponseType.ERC_1271;
        }
        else {
            return ResponseType.NONE;
        }
    }
    async createResponse(keys, responseType, publisherId, streamPartId, publicKey, recipientId, requestId, keyEncryptionType) {
        const encryptedGroupKeys = await Promise.all(keys.map(async (key) => ({
            id: key.id,
            data: await EncryptionUtil.encryptForPublicKey(key.data, publicKey, keyEncryptionType)
        })));
        const responseContent = {
            recipientId: toUserIdRaw(recipientId),
            requestId,
            groupKeys: encryptedGroupKeys,
            encryptionType: keyEncryptionType,
        };
        const response = this.messageSigner.createSignedMessage({
            messageId: new MessageID(StreamPartIDUtils.getStreamID(streamPartId), StreamPartIDUtils.getStreamPartition(streamPartId), Date.now(), 0, publisherId, createRandomMsgChainId()),
            content: GroupKeyResponse.toBinary(responseContent),
            contentType: ContentType.BINARY,
            messageType: StreamMessageType.GROUP_KEY_RESPONSE,
            encryptionType: EncryptionType.NONE,
        }, responseType === ResponseType.NORMAL ? this.identity.getSignatureType() : SignatureType.ERC_1271);
        return response;
    }
};
PublisherKeyExchange = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(5, inject(IdentityInjectionToken)),
    __param(6, inject(ConfigInjectionToken)),
    __metadata("design:paramtypes", [NetworkNodeFacade,
        StreamRegistry,
        SignatureValidator,
        MessageSigner,
        LocalGroupKeyStore,
        Identity, Object, StreamrClientEventEmitter,
        LoggerFactory])
], PublisherKeyExchange);
export { PublisherKeyExchange };
